import React, { useState, useEffect } from 'react';
import { Button, Row, Col, Form, Spinner } from 'react-bootstrap';
import { PatientServices } from '../../../services/PatientService';

const GlaucomaDetailsCard = ({ 
  patientId, 
  patientDetails, 
  hospitalId, 
  showAlert,
  formatDate
}) => {
  const [glaucomaData, setGlaucomaData] = useState({
    testConducted: [],
    diagnosed: '', // 'yes' or 'no'
    treatmentType: '', // 'prescribedMedication', 'laserRx', 'surgery', 'patientRefusedTreatment'
    // For Prescribed Medication
    prescribedMedication: '',
    prescribedTreatmentStatus: '', // 'followUp' or 'treatmentCompleted'
    prescribedTreatmentCompletionDate: '',
    prescribedTreatmentCompletionRemarks: '',
    // For Laser Rx and Surgery
    dateOfAdmission: '',
    dateOfSurgery: '',
    dischargeDate: '',
    procedureDone: '',
    surgeryTreatmentStatus: '', // 'followUp' or 'treatmentCompleted'
    surgeryTreatmentCompletionDate: '',
    surgeryTreatmentCompletionRemarks: '',
    surgeryPlaceOfFollowUp: '',
    surgeryFollowUpDate: '',
    surgeryFollowUpRemarks: '',
    insurance: [], // Array for multi-select
    customInsurance: '', // For when insurance includes "Others"
    // For Patient Refused Treatment
    reasonForRefusal: '',
    // For No diagnosis - with follow-up
    followUpPeriod: '',
    // Follow-up fields (used for all follow-up scenarios)
    placeOfFollowUp: '',
    followUpDate: '',
    actualVisitDate: '', // Compulsory when adding follow-up after initial diagnosis
    remarks: '',
    treatmentStatus: '', // 'followUp' or 'treatmentCompleted'
    treatmentCompletionDate: '',
    treatmentCompletionRemarks: ''
  });

  const [followupPlaces, setFollowupPlaces] = useState([]);

  const [submittedGlaucomaDetails, setSubmittedGlaucomaDetails] = useState(null);
  const [glaucomaFollowUpDetails, setGlaucomaFollowUpDetails] = useState([]);
  const [submitLoading, setSubmitLoading] = useState(false);

  useEffect(() => {
    if (patientId) {
      fetchExistingGlaucomaDetails();
      fetchFollowupPlaces();
    }
  }, [patientId]);

  const formatDDMMYYYY = (dateStr) => {
    if (!dateStr) return "";
    const [dd, mm, yyyy] = dateStr.split("-");
    return `${dd}-${mm}-${yyyy}`;
  };

  const fetchFollowupPlaces = async () => {
    try {
      const response = await PatientServices.getFollowupPlaces();
      // Response is directly an array
      const places = response || [];
      // Filter: SIMS and names starting with GH
      const filtered = places.filter(place => 
        place.name === 'SIMS' || place.name.startsWith('GH')
      );
      setFollowupPlaces(filtered);
    } catch (error) {
      console.error('Error details:', error.response || error.message);
    }
  };

  const fetchGlaucomaFollowUpDetails = async (glaucomaDetailsId) => {
    try {
      const response = await PatientServices.getGlaucomaFollowUpDetails(glaucomaDetailsId);
      if (response && response.data) {
        // Sort follow-ups by followUpDate in ascending order (oldest first)
        const sortedFollowUps = [...response.data].sort((a, b) => {
          return new Date(a.followUpDate) - new Date(b.followUpDate);
        });
        setGlaucomaFollowUpDetails(sortedFollowUps);
      }
    } catch (error) {
      console.error('Error details:', error.response || error.message);
    }
  };

  const fetchExistingGlaucomaDetails = async () => {
    try {
      const response = await PatientServices.getPatientGlaucomaDetailsByPatientId(patientId);
      if (response && response.data) {
        setSubmittedGlaucomaDetails(response.data);
        
        // Fetch follow-up details if glaucoma details exist
        // This includes: diagnosed=no with follow-up, or eyeProblemStatus = 1 (Follow Up) or 2 (Completed)
        if (response.data.id) {
          fetchGlaucomaFollowUpDetails(response.data.id);
        }
      }
    } catch (error) {
      console.error('Error fetching existing glaucoma details:', error);
      console.error('Error status:', error.response?.status);
      console.error('Error data:', error.response?.data);
      console.error('Error message:', error.message);
    }
  };

  const handleGlaucomaDataChange = (field, value) => {
    setGlaucomaData(prev => {
      let updated = { ...prev, [field]: value };

      // Handle test conducted checkboxes
      if (field === 'testConducted') {
        const currentTests = prev.testConducted || [];
        if (currentTests.includes(value)) {
          updated.testConducted = currentTests.filter(test => test !== value);
        } else {
          updated.testConducted = [...currentTests, value];
        }
      }

      // Handle insurance checkboxes
      if (field === 'insurance') {
        const currentInsurance = prev.insurance || [];
        if (currentInsurance.includes(value)) {
          updated.insurance = currentInsurance.filter(ins => ins !== value);
        } else {
          updated.insurance = [...currentInsurance, value];
        }
      }

      // Reset fields when diagnosis changes
      if (field === 'diagnosed') {
        updated = {
          ...updated,
          treatmentType: '',
          prescribedMedication: '',
          placeOfFollowUp: '',
          followUpDate: '',
          remarks: '',
          dateOfAdmission: '',
          dateOfSurgery: '',
          dischargeDate: '',
          procedureDone: '',
          surgeryPlaceOfFollowUp: '',
          surgeryFollowUpDate: '',
          surgeryFollowUpRemarks: '',
          insurance: [],
          customInsurance: '',
          reasonForRefusal: '',
          followUpPeriod: ''
        };
      }

      // Reset fields when treatment type changes
      if (field === 'treatmentType') {
        updated = {
          ...updated,
          prescribedMedication: '',
          prescribedTreatmentStatus: '',
          prescribedTreatmentCompletionDate: '',
          prescribedTreatmentCompletionRemarks: '',
          placeOfFollowUp: '',
          followUpDate: '',
          remarks: '',
          dateOfAdmission: '',
          dateOfSurgery: '',
          dischargeDate: '',
          procedureDone: '',
          surgeryTreatmentStatus: '',
          surgeryTreatmentCompletionDate: '',
          surgeryTreatmentCompletionRemarks: '',
          surgeryPlaceOfFollowUp: '',
          surgeryFollowUpDate: '',
          surgeryFollowUpRemarks: '',
          insurance: [],
          customInsurance: '',
          reasonForRefusal: ''
        };
      }
      
      // Reset fields when prescribed treatment status changes
      if (field === 'prescribedTreatmentStatus') {
        updated = {
          ...updated,
          placeOfFollowUp: '',
          followUpDate: '',
          remarks: '',
          prescribedTreatmentCompletionDate: '',
          prescribedTreatmentCompletionRemarks: ''
        };
      }
      
      // Reset fields when surgery treatment status changes
      if (field === 'surgeryTreatmentStatus') {
        updated = {
          ...updated,
          surgeryPlaceOfFollowUp: '',
          surgeryFollowUpDate: '',
          surgeryFollowUpRemarks: '',
          surgeryTreatmentCompletionDate: '',
          surgeryTreatmentCompletionRemarks: ''
        };
      }

      return updated;
    });
  };

  const handleGlaucomaSubmit = async () => {
    try {
      setSubmitLoading(true);
      
      // Check if this is a follow-up submission (actual visit date present OR treatmentStatus is set)
      if (submittedGlaucomaDetails && glaucomaData.actualVisitDate && glaucomaData.treatmentStatus) {
        
        // Validate actual visit date is compulsory
        if (!glaucomaData.actualVisitDate) {
          showAlert('Validation Error', 'Actual visit date is required.', 'warning');
          setSubmitLoading(false);
          return;
        }

        if (glaucomaData.treatmentStatus === 'followUp') {
          // Validate follow-up fields
          if (!glaucomaData.placeOfFollowUp || !glaucomaData.followUpDate || !glaucomaData.remarks) {
            showAlert('Validation Error', 'All follow-up fields are required.', 'warning');
            setSubmitLoading(false);
            return;
          }

          // Create follow-up record with actual visit date
          const followUpData = {
            id: submittedGlaucomaDetails.id,
            followUpDate: glaucomaData.followUpDate,
            actualVisitDate: glaucomaData.actualVisitDate,
            remarks: glaucomaData.remarks,
            placeOfFollowUpId: parseInt(glaucomaData.placeOfFollowUp)
          };
          
          await PatientServices.createGlaucomaFollowUp(followUpData);
          
          // Reset form and refresh data
          setGlaucomaData(prev => ({
            ...prev,
            actualVisitDate: '',
            followUpDate: '',
            treatmentStatus: '',
            remarks: '',
            placeOfFollowUp: ''
          }));
          
          // Refresh follow-up details
          fetchGlaucomaFollowUpDetails(submittedGlaucomaDetails.id);
          showAlert('Success', 'Follow-up details added successfully!', 'success');
        } else if (glaucomaData.treatmentStatus === 'diagnosedYes') {
          // Patient was diagnosed=no, now confirmed as glaucoma
          // Validate treatment type is selected
          if (!glaucomaData.treatmentType) {
            showAlert('Validation Error', 'Please select a treatment type.', 'warning');
            setSubmitLoading(false);
            return;
          }

          // Validate prescribed medication fields BEFORE updating
          if (glaucomaData.treatmentType === 'prescribedMedication') {
            if (!glaucomaData.placeOfFollowUp) {
              showAlert('Validation Error', 'Place of follow-up is required.', 'warning');
              setSubmitLoading(false);
              return;
            }
            if (!glaucomaData.followUpDate) {
              showAlert('Validation Error', 'Follow-up date is required.', 'warning');
              setSubmitLoading(false);
              return;
            }
            if (!glaucomaData.remarks) {
              showAlert('Validation Error', 'Remarks are required.', 'warning');
              setSubmitLoading(false);
              return;
            }
          }

          // Update glaucoma details to diagnosed=yes
          const updateData = {
            patientId: submittedGlaucomaDetails.patientDetails.id,
            hospitalId: submittedGlaucomaDetails.glaucomaReferredHospital.id,
            diagnosisType: 'glaucoma',
            testConducted: submittedGlaucomaDetails.testConducted || [],
            diagnosed: 'yes',
            treatmentType: glaucomaData.treatmentType,
            ...(glaucomaData.treatmentType === 'prescribedMedication' && {
              prescribedMedication: glaucomaData.prescribedMedication
            }),
            ...(glaucomaData.treatmentType === 'patientRefusedTreatment' && {
              reasonForRefusal: glaucomaData.reasonForRefusal
            })
          };

          const response = await PatientServices.updatePatientGlaucomaDetails(updateData);

          if (response && response.data) {
            // If prescribed medication, create follow-up
            if (glaucomaData.treatmentType === 'prescribedMedication') {
              const followUpData = {
                id: response.data.id,
                followUpDate: glaucomaData.followUpDate,
                actualVisitDate: glaucomaData.actualVisitDate,
                remarks: glaucomaData.remarks,
                placeOfFollowUpId: parseInt(glaucomaData.placeOfFollowUp)
              };

              await PatientServices.createGlaucomaFollowUp(followUpData);
            }

            // Refresh details
            await fetchExistingGlaucomaDetails();
            
            // Reset form
            setGlaucomaData(prev => ({
              ...prev,
              actualVisitDate: '',
              treatmentStatus: '',
              treatmentType: '',
              prescribedMedication: '',
              placeOfFollowUp: '',
              followUpDate: '',
              remarks: '',
              reasonForRefusal: ''
            }));

            showAlert('Success', 'Glaucoma diagnosis updated to Yes successfully!', 'success');
          }
          
          setSubmitLoading(false);
          return;
        } else if (glaucomaData.treatmentStatus === 'treatmentCompleted') {
          // Validate treatment completion fields
          if (!glaucomaData.treatmentCompletionDate || !glaucomaData.treatmentCompletionRemarks) {
            showAlert('Validation Error', 'Treatment completion date and remarks are required.', 'warning');
            setSubmitLoading(false);
            return;
          }

          // Update glaucoma details to mark as completed
          const updateData = {
            patientId: submittedGlaucomaDetails.patientDetails.id,
            hospitalId: submittedGlaucomaDetails.glaucomaReferredHospital.id,
            diagnosisType: 'glaucoma',
            testConducted: submittedGlaucomaDetails.testConducted || [],
            diagnosed: submittedGlaucomaDetails.diagnosed,
            treatmentType: submittedGlaucomaDetails.treatmentType,
            prescribedMedication: submittedGlaucomaDetails.prescribedMedication,
            dateOfAdmission: submittedGlaucomaDetails.dateOfAdmission,
            dateOfSurgery: submittedGlaucomaDetails.dateOfSurgery,
            dischargeDate: submittedGlaucomaDetails.dischargeDate,
            procedureDone: submittedGlaucomaDetails.procedureDone,
            insurance: submittedGlaucomaDetails.insurance,
            reasonForRefusal: submittedGlaucomaDetails.reasonForRefusal,
            treatmentStatus: 'treatmentCompleted',
            treatmentCompletionDate: glaucomaData.treatmentCompletionDate,
            treatmentCompletionRemarks: glaucomaData.treatmentCompletionRemarks,
            actualVisitDate: glaucomaData.actualVisitDate
          };
          
          const response = await PatientServices.updatePatientGlaucomaDetails(updateData);
          if (response && response.data) {
            // Refresh details
            await fetchExistingGlaucomaDetails();
            
            // Reset form
            setGlaucomaData(prev => ({
              ...prev,
              actualVisitDate: '',
              treatmentStatus: '',
              treatmentCompletionDate: '',
              treatmentCompletionRemarks: ''
            }));
            
            showAlert('Success', 'Treatment marked as completed!', 'success');
          }
        }
        
        setSubmitLoading(false);
        return;
      }
      
      // For Prescribed Medication, create follow-up record (INITIAL SUBMISSION ONLY)
      if (glaucomaData.treatmentType === 'prescribedMedication' && !submittedGlaucomaDetails) {
        // Validate treatment status selection
        if (!glaucomaData.prescribedTreatmentStatus) {
          showAlert('Validation Error', 'Please select treatment status (Follow Up or Treatment Completed).', 'warning');
          setSubmitLoading(false);
          return;
        }

        // Validate based on treatment status
        if (glaucomaData.prescribedTreatmentStatus === 'followUp') {
          if (!glaucomaData.placeOfFollowUp) {
            showAlert('Validation Error', 'Place of follow-up is required.', 'warning');
            setSubmitLoading(false);
            return;
          }
          if (!glaucomaData.followUpDate) {
            showAlert('Validation Error', 'Follow-up date is required.', 'warning');
            setSubmitLoading(false);
            return;
          }
          if (!glaucomaData.remarks) {
            showAlert('Validation Error', 'Remarks are required.', 'warning');
            setSubmitLoading(false);
            return;
          }
        } else if (glaucomaData.prescribedTreatmentStatus === 'treatmentCompleted') {
          if (!glaucomaData.prescribedTreatmentCompletionDate) {
            showAlert('Validation Error', 'Treatment completion date is required.', 'warning');
            setSubmitLoading(false);
            return;
          }
          if (!glaucomaData.prescribedTreatmentCompletionRemarks) {
            showAlert('Validation Error', 'Remarks are required.', 'warning');
            setSubmitLoading(false);
            return;
          }
        }

        // First, create glaucoma details (INITIAL SUBMISSION ONLY)
        const glaucomaSubmitData = {
          patientId: patientDetails.id,
          hospitalId: hospitalId,
          diagnosisType: 'glaucoma',
          testConducted: glaucomaData.testConducted,
          diagnosed: glaucomaData.diagnosed,
          treatmentType: glaucomaData.treatmentType,
          prescribedMedication: glaucomaData.prescribedMedication
        };

        const glaucomaResponse = await PatientServices.createPatientGlaucomaDetails(glaucomaSubmitData);
        
        if (glaucomaResponse && glaucomaResponse.data) {
          if (glaucomaData.prescribedTreatmentStatus === 'followUp') {
            // Create follow-up record
            const followUpData = {
              id: glaucomaResponse.data.id,
              followUpDate: glaucomaData.followUpDate,
              remarks: glaucomaData.remarks,
              placeOfFollowUpId: parseInt(glaucomaData.placeOfFollowUp)
            };

            await PatientServices.createGlaucomaFollowUp(followUpData);
            showAlert('Success', 'Glaucoma details and follow-up saved successfully!', 'success');
          } else if (glaucomaData.prescribedTreatmentStatus === 'treatmentCompleted') {
            // Mark as treatment completed
            const updateData = {
              patientId: patientDetails.id,
              hospitalId: hospitalId,
              diagnosisType: 'glaucoma',
              testConducted: glaucomaData.testConducted,
              diagnosed: glaucomaData.diagnosed,
              treatmentType: glaucomaData.treatmentType,
              prescribedMedication: glaucomaData.prescribedMedication,
              treatmentStatus: 'treatmentCompleted',
              treatmentCompletionDate: glaucomaData.prescribedTreatmentCompletionDate,
              treatmentCompletionRemarks: glaucomaData.prescribedTreatmentCompletionRemarks
            };
            
            await PatientServices.updatePatientGlaucomaDetails(updateData);
            showAlert('Success', 'Glaucoma details saved and treatment marked as completed!', 'success');
          }
          
          // Refresh glaucoma details and follow-up details
          await fetchExistingGlaucomaDetails();
          
          // Clear form fields
          setGlaucomaData(prev => ({
            ...prev,
            placeOfFollowUp: '',
            followUpDate: '',
            remarks: '',
            prescribedMedication: '',
            prescribedTreatmentStatus: '',
            prescribedTreatmentCompletionDate: '',
            prescribedTreatmentCompletionRemarks: '',
            treatmentType: '',
            diagnosed: ''
          }));
        }
      } else if ((glaucomaData.treatmentType === 'laserRx' || glaucomaData.treatmentType === 'surgery') && !submittedGlaucomaDetails) {
        // Validate required surgery dates
        if (!glaucomaData.dateOfAdmission) {
          showAlert('Validation Error', 'Date of admission is required.', 'warning');
          setSubmitLoading(false);
          return;
        }
        if (!glaucomaData.dateOfSurgery) {
          showAlert('Validation Error', 'Date of surgery is required.', 'warning');
          setSubmitLoading(false);
          return;
        }
        if (!glaucomaData.dischargeDate) {
          showAlert('Validation Error', 'Discharge date is required.', 'warning');
          setSubmitLoading(false);
          return;
        }
        
        // Validate treatment status selection
        if (!glaucomaData.surgeryTreatmentStatus) {
          showAlert('Validation Error', 'Please select treatment status (Follow Up or Treatment Completed).', 'warning');
          setSubmitLoading(false);
          return;
        }

        // Validate based on treatment status
        if (glaucomaData.surgeryTreatmentStatus === 'followUp') {
          if (!glaucomaData.surgeryPlaceOfFollowUp) {
            showAlert('Validation Error', 'Place of follow-up is required.', 'warning');
            setSubmitLoading(false);
            return;
          }
          if (!glaucomaData.surgeryFollowUpDate) {
            showAlert('Validation Error', 'Follow-up date is required.', 'warning');
            setSubmitLoading(false);
            return;
          }
          if (!glaucomaData.surgeryFollowUpRemarks) {
            showAlert('Validation Error', 'Remarks are required.', 'warning');
            setSubmitLoading(false);
            return;
          }
        } else if (glaucomaData.surgeryTreatmentStatus === 'treatmentCompleted') {
          if (!glaucomaData.surgeryTreatmentCompletionDate) {
            showAlert('Validation Error', 'Treatment completion date is required.', 'warning');
            setSubmitLoading(false);
            return;
          }
          if (!glaucomaData.surgeryTreatmentCompletionRemarks) {
            showAlert('Validation Error', 'Remarks are required.', 'warning');
            setSubmitLoading(false);
            return;
          }
        }

        const insuranceValue = glaucomaData.insurance.includes('Others') 
          ? [...glaucomaData.insurance.filter(i => i !== 'Others'), glaucomaData.customInsurance].join(', ')
          : glaucomaData.insurance.join(', ');

        const submitData = {
          patientId: patientDetails.id,
          hospitalId: hospitalId,
          diagnosisType: 'glaucoma',
          testConducted: glaucomaData.testConducted,
          diagnosed: glaucomaData.diagnosed,
          treatmentType: glaucomaData.treatmentType,
          dateOfAdmission: glaucomaData.dateOfAdmission,
          dateOfSurgery: glaucomaData.dateOfSurgery,
          dischargeDate: glaucomaData.dischargeDate,
          procedureDone: glaucomaData.procedureDone,
          insurance: insuranceValue
        };

        const glaucomaResponse = await PatientServices.createPatientGlaucomaDetails(submitData);
        
        if (glaucomaResponse && glaucomaResponse.data) {
          if (glaucomaData.surgeryTreatmentStatus === 'followUp') {
            // Create follow-up record
            const followUpData = {
              id: glaucomaResponse.data.id,
              followUpDate: glaucomaData.surgeryFollowUpDate,
              remarks: glaucomaData.surgeryFollowUpRemarks,
              placeOfFollowUpId: parseInt(glaucomaData.surgeryPlaceOfFollowUp)
            };

            await PatientServices.createGlaucomaFollowUp(followUpData);
            showAlert('Success', 'Glaucoma details and follow-up saved successfully!', 'success');
          } else if (glaucomaData.surgeryTreatmentStatus === 'treatmentCompleted') {
            // Mark as treatment completed
            const updateData = {
              patientId: patientDetails.id,
              hospitalId: hospitalId,
              diagnosisType: 'glaucoma',
              testConducted: glaucomaData.testConducted,
              diagnosed: glaucomaData.diagnosed,
              treatmentType: glaucomaData.treatmentType,
              dateOfAdmission: glaucomaData.dateOfAdmission,
              dateOfSurgery: glaucomaData.dateOfSurgery,
              dischargeDate: glaucomaData.dischargeDate,
              procedureDone: glaucomaData.procedureDone,
              insurance: insuranceValue,
              treatmentStatus: 'treatmentCompleted',
              treatmentCompletionDate: glaucomaData.surgeryTreatmentCompletionDate,
              treatmentCompletionRemarks: glaucomaData.surgeryTreatmentCompletionRemarks
            };
            
            await PatientServices.updatePatientGlaucomaDetails(updateData);
            showAlert('Success', 'Glaucoma details saved and treatment marked as completed!', 'success');
          }
          
          // Refresh glaucoma details and follow-up details
          await fetchExistingGlaucomaDetails();
          
          // Clear form fields
          setGlaucomaData(prev => ({
            ...prev,
            dateOfAdmission: '',
            dateOfSurgery: '',
            dischargeDate: '',
            procedureDone: '',
            surgeryTreatmentStatus: '',
            surgeryTreatmentCompletionDate: '',
            surgeryTreatmentCompletionRemarks: '',
            surgeryPlaceOfFollowUp: '',
            surgeryFollowUpDate: '',
            surgeryFollowUpRemarks: '',
            insurance: [],
            customInsurance: '',
            treatmentType: '',
            diagnosed: ''
          }));
        }
      } else if (glaucomaData.diagnosed === 'no' && !submittedGlaucomaDetails) {
        // For Diagnosed = No with follow-up
        // Validate required fields
        if (!glaucomaData.placeOfFollowUp) {
          showAlert('Validation Error', 'Place of follow-up is required.', 'warning');
          setSubmitLoading(false);
          return;
        }
        if (!glaucomaData.followUpDate) {
          showAlert('Validation Error', 'Follow-up date is required.', 'warning');
          setSubmitLoading(false);
          return;
        }
        if (!glaucomaData.remarks) {
          showAlert('Validation Error', 'Remarks are required.', 'warning');
          setSubmitLoading(false);
          return;
        }

        // First, create/update glaucoma details
        const glaucomaSubmitData = {
          patientId: patientDetails.id,
          hospitalId: hospitalId,
          diagnosisType: 'glaucoma',
          testConducted: glaucomaData.testConducted,
          diagnosed: glaucomaData.diagnosed
        };

        const glaucomaResponse = await PatientServices.createPatientGlaucomaDetails(glaucomaSubmitData);
        
        if (glaucomaResponse && glaucomaResponse.data) {
          // Then create follow-up record
          const followUpData = {
            id: glaucomaResponse.data.id,
            followUpDate: glaucomaData.followUpDate,
            remarks: glaucomaData.remarks,
            placeOfFollowUpId: parseInt(glaucomaData.placeOfFollowUp)
          };

          await PatientServices.createGlaucomaFollowUp(followUpData);
          
          // Refresh glaucoma details and follow-up details
          await fetchExistingGlaucomaDetails();
          
          // Clear form fields
          setGlaucomaData(prev => ({
            ...prev,
            placeOfFollowUp: '',
            followUpDate: '',
            remarks: '',
            diagnosed: ''
          }));
          
          showAlert('Success', 'Glaucoma details and follow-up saved successfully!', 'success');
        }
      } else if (!submittedGlaucomaDetails) {
        // For patient refused treatment (INITIAL SUBMISSION ONLY)
        const submitData = {
          patientId: patientDetails.id,
          hospitalId: hospitalId,
          diagnosisType: 'glaucoma',
          testConducted: glaucomaData.testConducted,
          diagnosed: glaucomaData.diagnosed,
          treatmentType: glaucomaData.treatmentType,
          reasonForRefusal: glaucomaData.reasonForRefusal
        };

        const response = await PatientServices.createPatientGlaucomaDetails(submitData);
        
        if (response && response.data) {
          setSubmittedGlaucomaDetails(response.data);
          showAlert('Success', 'Glaucoma diagnosis updated successfully!', 'success');
        }
      }
    } catch (error) {
      console.error('Error submitting glaucoma data:', error);
      showAlert('Error', 'Failed to update glaucoma diagnosis. Please try again.', 'danger');
    } finally {
      setSubmitLoading(false);
    }
  };

  return (
    <>
      {/* Show form only if no submitted details exist */}
      {!submittedGlaucomaDetails && (
        <>
          {/* Test Conducted - Multi-select checkboxes */}
          <Row className="mb-3">
            <Col>
              <Form.Label style={{ fontWeight: 'bold', marginBottom: '10px' }}>Test Conducted :</Form.Label>
              <div className="d-flex flex-wrap gap-3">
                <Form.Check
                  type="checkbox"
                  id="test-applanation"
                  label="Applanation Tonometry"
                  checked={glaucomaData.testConducted?.includes('Applanation Tonometry')}
                  onChange={() => handleGlaucomaDataChange('testConducted', 'Applanation Tonometry')}
                />
                <Form.Check
                  type="checkbox"
                  id="test-hfa"
                  label="HFA"
                  checked={glaucomaData.testConducted?.includes('HFA')}
                  onChange={() => handleGlaucomaDataChange('testConducted', 'HFA')}
                />
                <Form.Check
                  type="checkbox"
                  id="test-fundus"
                  label="Fundus Examination"
                  checked={glaucomaData.testConducted?.includes('Fundus Examination')}
                  onChange={() => handleGlaucomaDataChange('testConducted', 'Fundus Examination')}
                />
                <Form.Check
                  type="checkbox"
                  id="test-glaucoma-oct"
                  label="Glaucoma OCT"
                  checked={glaucomaData.testConducted?.includes('Glaucoma OCT')}
                  onChange={() => handleGlaucomaDataChange('testConducted', 'Glaucoma OCT')}
                />
                <Form.Check
                  type="checkbox"
                  id="test-gonioscopy"
                  label="Gonioscopy"
                  checked={glaucomaData.testConducted?.includes('Gonioscopy')}
                  onChange={() => handleGlaucomaDataChange('testConducted', 'Gonioscopy')}
                />
                <Form.Check
                  type="checkbox"
                  id="test-others"
                  label="Others"
                  checked={glaucomaData.testConducted?.includes('Others')}
                  onChange={() => handleGlaucomaDataChange('testConducted', 'Others')}
                />
              </div>
            </Col>
          </Row>

          {/* Glaucoma Diagnosed */}
          <Row className="mb-3">
            <Col>
              <Form.Label style={{ fontWeight: 'bold', marginBottom: '10px' }}>Glaucoma Diagnosed :</Form.Label>
              <div className="d-flex gap-4">
                <Form.Check
                  type="radio"
                  id="glaucoma-diagnosed-yes"
                  name="glaucomaDiagnosed"
                  label="Yes"
                  checked={glaucomaData.diagnosed === 'yes'}
                  onChange={() => handleGlaucomaDataChange('diagnosed', 'yes')}
                />
                <Form.Check
                  type="radio"
                  id="glaucoma-diagnosed-no"
                  name="glaucomaDiagnosed"
                  label="No"
                  checked={glaucomaData.diagnosed === 'no'}
                  onChange={() => handleGlaucomaDataChange('diagnosed', 'no')}
                />
              </div>
            </Col>
          </Row>

          {/* When Glaucoma Diagnosed is Yes */}
          {glaucomaData.diagnosed === 'yes' && (
            <>
              {/* Treatment Type Selection */}
              <Row className="mb-3">
                <Col>
                  <div className="d-flex flex-wrap gap-4">
                    <Form.Check
                      type="radio"
                      id="treatment-prescribed-medication"
                      name="treatmentType"
                      label="Prescribed Medication"
                      checked={glaucomaData.treatmentType === 'prescribedMedication'}
                      onChange={() => handleGlaucomaDataChange('treatmentType', 'prescribedMedication')}
                    />
                    <Form.Check
                      type="radio"
                      id="treatment-surgery"
                      name="treatmentType"
                      label="Surgery"
                      checked={glaucomaData.treatmentType === 'surgery'}
                      onChange={() => handleGlaucomaDataChange('treatmentType', 'surgery')}
                    />
                    <Form.Check
                      type="radio"
                      id="treatment-laser-rx"
                      name="treatmentType"
                      label="Laser Rx"
                      checked={glaucomaData.treatmentType === 'laserRx'}
                      onChange={() => handleGlaucomaDataChange('treatmentType', 'laserRx')}
                    />
                    <Form.Check
                      type="radio"
                      id="treatment-patient-refused"
                      name="treatmentType"
                      label="Patients Refused Treatment"
                      checked={glaucomaData.treatmentType === 'patientRefusedTreatment'}
                      onChange={() => handleGlaucomaDataChange('treatmentType', 'patientRefusedTreatment')}
                    />
                  </div>
                </Col>
              </Row>

              {/* Prescribed Medication Fields */}
              {glaucomaData.treatmentType === 'prescribedMedication' && (
                <>
                  <Row className="mb-3">
                    <Col md={4}>
                      <Form.Label>Prescribed medication :</Form.Label>
                      <Form.Select
                        value={glaucomaData.prescribedMedication}
                        onChange={(e) => handleGlaucomaDataChange('prescribedMedication', e.target.value)}
                      >
                        <option value="">Select Medication</option>
                        <option value="Timolol">Timolol</option>
                        <option value="Latanoprost">Latanoprost</option>
                        <option value="Dorzolamide">Dorzolamide</option>
                        <option value="Others - To specify">Others - To specify</option>
                      </Form.Select>
                    </Col>
                  </Row>
                  
                  {/* Treatment Status Options */}
                  <Row className="mb-3">
                    <Col md={12}>
                      <Form.Label><span style={{ color: 'red' }}>*</span> Treatment Status :</Form.Label>
                      <div className="d-flex gap-4">
                        <div>
                          <Form.Check
                            type="radio"
                            id="prescribed-followup"
                            name="prescribedTreatmentStatus"
                            label="Follow Up"
                            checked={glaucomaData.prescribedTreatmentStatus === 'followUp'}
                            onChange={() => handleGlaucomaDataChange('prescribedTreatmentStatus', 'followUp')}
                          />
                        </div>
                        <div>
                          <Form.Check
                            type="radio"
                            id="prescribed-completed"
                            name="prescribedTreatmentStatus"
                            label="Treatment Completed"
                            checked={glaucomaData.prescribedTreatmentStatus === 'treatmentCompleted'}
                            onChange={() => handleGlaucomaDataChange('prescribedTreatmentStatus', 'treatmentCompleted')}
                          />
                        </div>
                      </div>
                    </Col>
                  </Row>

                  {/* Follow Up Fields */}
                  {glaucomaData.prescribedTreatmentStatus === 'followUp' && (
                    <>
                      <Row className="mb-3">
                        <Col md={4}>
                          <Form.Label><span style={{ color: 'red' }}>*</span> Place of follow up :</Form.Label>
                          <Form.Select
                            value={glaucomaData.placeOfFollowUp}
                            onChange={(e) => handleGlaucomaDataChange('placeOfFollowUp', e.target.value)}
                            required
                          >
                            <option value="">Select Place</option>
                            {followupPlaces.map(place => (
                              <option key={place.id} value={place.id}>{place.name}</option>
                            ))}
                          </Form.Select>
                        </Col>
                        <Col md={4}>
                          <Form.Label><span style={{ color: 'red' }}>*</span> Follow-up Date :</Form.Label>
                          <Form.Control
                            type="date"
                            value={glaucomaData.followUpDate}
                            onChange={(e) => handleGlaucomaDataChange('followUpDate', e.target.value)}
                            required
                          />
                        </Col>
                      </Row>
                      <Row className="mb-3">
                        <Col md={12}>
                          <Form.Label><span style={{ color: 'red' }}>*</span> Remarks :</Form.Label>
                          <Form.Control
                            type="text"
                            value={glaucomaData.remarks}
                            onChange={(e) => handleGlaucomaDataChange('remarks', e.target.value)}
                            placeholder="Enter remarks"
                            required
                          />
                        </Col>
                      </Row>
                    </>
                  )}

                  {/* Treatment Completed Fields */}
                  {glaucomaData.prescribedTreatmentStatus === 'treatmentCompleted' && (
                    <>
                      <Row className="mb-3">
                        <Col md={4}>
                          <Form.Label><span style={{ color: 'red' }}>*</span> Treatment Completion Date :</Form.Label>
                          <Form.Control
                            type="date"
                            value={glaucomaData.prescribedTreatmentCompletionDate}
                            onChange={(e) => handleGlaucomaDataChange('prescribedTreatmentCompletionDate', e.target.value)}
                            required
                          />
                        </Col>
                      </Row>
                      <Row className="mb-3">
                        <Col md={12}>
                          <Form.Label><span style={{ color: 'red' }}>*</span> Remarks :</Form.Label>
                          <Form.Control
                            type="text"
                            value={glaucomaData.prescribedTreatmentCompletionRemarks}
                            onChange={(e) => handleGlaucomaDataChange('prescribedTreatmentCompletionRemarks', e.target.value)}
                            placeholder="Enter remarks"
                            required
                          />
                        </Col>
                      </Row>
                    </>
                  )}
                </>
              )}

              {/* Laser Rx and Surgery Fields */}
              {(glaucomaData.treatmentType === 'laserRx' || glaucomaData.treatmentType === 'surgery') && (
                <>
                  <Row className="mb-3">
                    <Col md={3}>
                      <Form.Label><span style={{ color: 'red' }}>*</span> Date of Admission :</Form.Label>
                      <Form.Control
                        type="date"
                        value={glaucomaData.dateOfAdmission}
                        onChange={(e) => handleGlaucomaDataChange('dateOfAdmission', e.target.value)}
                        required
                      />
                    </Col>
                    <Col md={3}>
                      <Form.Label><span style={{ color: 'red' }}>*</span> Date of Surgery :</Form.Label>
                      <Form.Control
                        type="date"
                        value={glaucomaData.dateOfSurgery}
                        min={glaucomaData.dateOfAdmission || undefined}
                        onChange={(e) => {
                          const selectedDate = e.target.value;
                          if (glaucomaData.dateOfAdmission && selectedDate < glaucomaData.dateOfAdmission) {
                            showAlert('Validation Error', 'Surgery date must be on or after the admission date.', 'warning');
                            return;
                          }
                          handleGlaucomaDataChange('dateOfSurgery', selectedDate);
                        }}
                        required
                      />
                    </Col>
                    <Col md={3}>
                      <Form.Label><span style={{ color: 'red' }}>*</span> Discharge Date :</Form.Label>
                      <Form.Control
                        type="date"
                        value={glaucomaData.dischargeDate}
                        min={glaucomaData.dateOfSurgery || glaucomaData.dateOfAdmission || undefined}
                        onChange={(e) => {
                          const selectedDate = e.target.value;
                          const minDate = glaucomaData.dateOfSurgery || glaucomaData.dateOfAdmission;
                          if (minDate && selectedDate < minDate) {
                            showAlert('Validation Error', 'Discharge date must be on or after the surgery or admission date.', 'warning');
                            return;
                          }
                          handleGlaucomaDataChange('dischargeDate', selectedDate);
                        }}
                        required
                      />
                    </Col>
                    <Col md={3}>
                      <Form.Label>Procedure Done :</Form.Label>
                      <Form.Control
                        type="text"
                        value={glaucomaData.procedureDone}
                        onChange={(e) => handleGlaucomaDataChange('procedureDone', e.target.value)}
                        placeholder="Enter procedure"
                      />
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={12}>
                      <Form.Label>Insurance :</Form.Label>
                      <div className="d-flex flex-wrap gap-3">
                        <Form.Check
                          type="checkbox"
                          id="glaucoma-insurance-bpl"
                          label="BPL"
                          checked={glaucomaData.insurance?.includes('BPL')}
                          onChange={() => handleGlaucomaDataChange('insurance', 'BPL')}
                        />
                        <Form.Check
                          type="checkbox"
                          id="glaucoma-insurance-yashaswini"
                          label="Yashaswini"
                          checked={glaucomaData.insurance?.includes('Yashaswini')}
                          onChange={() => handleGlaucomaDataChange('insurance', 'Yashaswini')}
                        />
                        <Form.Check
                          type="checkbox"
                          id="glaucoma-insurance-esi"
                          label="ESI"
                          checked={glaucomaData.insurance?.includes('ESI')}
                          onChange={() => handleGlaucomaDataChange('insurance', 'ESI')}
                        />
                        <Form.Check
                          type="checkbox"
                          id="glaucoma-insurance-dharmastala"
                          label="Dharmastala"
                          checked={glaucomaData.insurance?.includes('Dharmastala')}
                          onChange={() => handleGlaucomaDataChange('insurance', 'Dharmastala')}
                        />
                        <Form.Check
                          type="checkbox"
                          id="glaucoma-insurance-bcs"
                          label="BCS"
                          checked={glaucomaData.insurance?.includes('BCS')}
                          onChange={() => handleGlaucomaDataChange('insurance', 'BCS')}
                        />
                        <Form.Check
                          type="checkbox"
                          id="glaucoma-insurance-others"
                          label="Others"
                          checked={glaucomaData.insurance?.includes('Others')}
                          onChange={() => handleGlaucomaDataChange('insurance', 'Others')}
                        />
                      </div>
                    </Col>
                  </Row>
                  {glaucomaData.insurance?.includes('Others') && (
                    <Row className="mb-3">
                      <Col md={4}>
                        <Form.Label>Specify Insurance :</Form.Label>
                        <Form.Control
                          type="text"
                          value={glaucomaData.customInsurance}
                          onChange={(e) => handleGlaucomaDataChange('customInsurance', e.target.value)}
                          placeholder="Enter insurance details"
                        />
                      </Col>
                    </Row>
                  )}

                  {/* Treatment Status Options */}
                  <Row className="mb-3">
                    <Col md={12}>
                      <Form.Label><span style={{ color: 'red' }}>*</span> Treatment Status :</Form.Label>
                      <div className="d-flex gap-4">
                        <div>
                          <Form.Check
                            type="radio"
                            id="surgery-followup"
                            name="surgeryTreatmentStatus"
                            label="Follow Up"
                            checked={glaucomaData.surgeryTreatmentStatus === 'followUp'}
                            onChange={() => handleGlaucomaDataChange('surgeryTreatmentStatus', 'followUp')}
                          />
                        </div>
                        <div>
                          <Form.Check
                            type="radio"
                            id="surgery-completed"
                            name="surgeryTreatmentStatus"
                            label="Treatment Completed"
                            checked={glaucomaData.surgeryTreatmentStatus === 'treatmentCompleted'}
                            onChange={() => handleGlaucomaDataChange('surgeryTreatmentStatus', 'treatmentCompleted')}
                          />
                        </div>
                      </div>
                    </Col>
                  </Row>

                  {/* Follow Up Fields */}
                  {glaucomaData.surgeryTreatmentStatus === 'followUp' && (
                    <Row className="mb-3">
                      <Col md={4}>
                        <Form.Label><span style={{ color: 'red' }}>*</span> Place of Follow-up :</Form.Label>
                        <Form.Select
                          value={glaucomaData.surgeryPlaceOfFollowUp}
                          onChange={(e) => handleGlaucomaDataChange('surgeryPlaceOfFollowUp', e.target.value)}
                          required
                        >
                          <option value="">Select Place</option>
                          {followupPlaces.map(place => (
                            <option key={place.id} value={place.id}>{place.name}</option>
                          ))}
                        </Form.Select>
                      </Col>
                      <Col md={4}>
                        <Form.Label><span style={{ color: 'red' }}>*</span> Follow-up Date :</Form.Label>
                        <Form.Control
                          type="date"
                          value={glaucomaData.surgeryFollowUpDate}
                          min={glaucomaData.dischargeDate || glaucomaData.dateOfSurgery || glaucomaData.dateOfAdmission || undefined}
                          onChange={(e) => {
                            const selectedDate = e.target.value;
                            const minDate = glaucomaData.dischargeDate || glaucomaData.dateOfSurgery || glaucomaData.dateOfAdmission;
                            if (minDate && selectedDate < minDate) {
                              showAlert('Validation Error', 'Follow-up date must be after the discharge, surgery, or admission date.', 'warning');
                              return;
                            }
                            handleGlaucomaDataChange('surgeryFollowUpDate', selectedDate);
                          }}
                          required
                        />
                      </Col>
                      <Col md={4}>
                        <Form.Label><span style={{ color: 'red' }}>*</span> Remarks :</Form.Label>
                        <Form.Control
                          type="text"
                          value={glaucomaData.surgeryFollowUpRemarks}
                          onChange={(e) => handleGlaucomaDataChange('surgeryFollowUpRemarks', e.target.value)}
                          placeholder="Enter remarks"
                          required
                        />
                      </Col>
                    </Row>
                  )}

                  {/* Treatment Completed Fields */}
                  {glaucomaData.surgeryTreatmentStatus === 'treatmentCompleted' && (
                    <>
                      <Row className="mb-3">
                        <Col md={4}>
                          <Form.Label><span style={{ color: 'red' }}>*</span> Treatment Completion Date :</Form.Label>
                          <Form.Control
                            type="date"
                            value={glaucomaData.surgeryTreatmentCompletionDate}
                            onChange={(e) => handleGlaucomaDataChange('surgeryTreatmentCompletionDate', e.target.value)}
                            required
                          />
                        </Col>
                      </Row>
                      <Row className="mb-3">
                        <Col md={12}>
                          <Form.Label><span style={{ color: 'red' }}>*</span> Remarks :</Form.Label>
                          <Form.Control
                            type="text"
                            value={glaucomaData.surgeryTreatmentCompletionRemarks}
                            onChange={(e) => handleGlaucomaDataChange('surgeryTreatmentCompletionRemarks', e.target.value)}
                            placeholder="Enter remarks"
                            required
                          />
                        </Col>
                      </Row>
                    </>
                  )}
                </>
              )}

              {/* Patient Refused Treatment */}
              {glaucomaData.treatmentType === 'patientRefusedTreatment' && (
                <Row className="mb-3">
                  <Col md={4}>
                    <Form.Label>Reason for Refusal :</Form.Label>
                    <Form.Control
                      type="text"
                      value={glaucomaData.reasonForRefusal}
                      onChange={(e) => handleGlaucomaDataChange('reasonForRefusal', e.target.value)}
                      placeholder="Enter reason"
                    />
                  </Col>
                </Row>
              )}
            </>
          )}

          {/* When Glaucoma Diagnosed is No - Follow-up fields */}
          {glaucomaData.diagnosed === 'no' && (
            <>
              <Row className="mb-3">
                <Col md={4}>
                  <Form.Label><span style={{ color: 'red' }}>*</span> Place of Follow-up :</Form.Label>
                  <Form.Select
                    value={glaucomaData.placeOfFollowUp}
                    onChange={(e) => handleGlaucomaDataChange('placeOfFollowUp', e.target.value)}
                    required
                  >
                    <option value="">Select Place</option>
                    {followupPlaces.map(place => (
                      <option key={place.id} value={place.id}>{place.name}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={4}>
                  <Form.Label><span style={{ color: 'red' }}>*</span> Follow-up Date :</Form.Label>
                  <Form.Control
                    type="date"
                    value={glaucomaData.followUpDate}
                    onChange={(e) => handleGlaucomaDataChange('followUpDate', e.target.value)}
                    required
                  />
                </Col>
                <Col md={4}>
                  <Form.Label><span style={{ color: 'red' }}>*</span> Remarks :</Form.Label>
                  <Form.Control
                    type="text"
                    value={glaucomaData.remarks}
                    onChange={(e) => handleGlaucomaDataChange('remarks', e.target.value)}
                    placeholder="Enter remarks"
                    required
                  />
                </Col>
              </Row>
            </>
          )}

          {/* Submit Button */}
          <Row className="mt-4">
            <Col className="d-flex justify-content-end">
              <Button 
                variant="success" 
                onClick={handleGlaucomaSubmit}
                disabled={submitLoading}
                style={{ 
                  padding: '0.5rem 2rem',
                  fontSize: '1rem'
                }}
              >
                {submitLoading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Submitting...
                  </>
                ) : (
                  'Submit'
                )}
              </Button>
            </Col>
          </Row>
        </>
      )}

      {/* Follow-up Management Section - For adding actual visit and next steps */}
      {submittedGlaucomaDetails && 
        ((submittedGlaucomaDetails.eyeProblemStatus && submittedGlaucomaDetails.eyeProblemStatus.id === 1) ||
         (submittedGlaucomaDetails.diagnosed === 'no' && glaucomaFollowUpDetails.length > 0) ||
         (glaucomaFollowUpDetails.length > 0 && (!submittedGlaucomaDetails.eyeProblemStatus || submittedGlaucomaDetails.eyeProblemStatus.id !== 2))) && (
        <>
          <hr className="my-4" />
          <h5 className="mb-3">Glaucoma Details</h5>
          
          {/* Display Submitted Glaucoma Treatment Details */}
          {submittedGlaucomaDetails && (
            <div className="mb-4" style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '5px' }}>
              <Row className="mb-2">
                <Col md={3}>
                  <Form.Label style={{ fontWeight: 'bold' }}>Diagnosed:</Form.Label>
                  <Form.Control
                    type="text"
                    value={submittedGlaucomaDetails.diagnosed === 'yes' ? 'Yes' : 'No'}
                    readOnly
                    style={{ backgroundColor: '#ffffff' }}
                  />
                </Col>
                {submittedGlaucomaDetails.diagnosed === 'yes' && (
                  <Col md={3}>
                    <Form.Label style={{ fontWeight: 'bold' }}>Treatment Type:</Form.Label>
                    <Form.Control
                      type="text"
                      value={
                        submittedGlaucomaDetails.treatmentType === 'prescribedMedication' ? 'Prescribed Medication' :
                        submittedGlaucomaDetails.treatmentType === 'laserRx' ? 'Laser Rx' :
                        submittedGlaucomaDetails.treatmentType === 'surgery' ? 'Surgery' :
                        submittedGlaucomaDetails.treatmentType === 'patientRefusedTreatment' ? 'Patient Refused Treatment' : 'N/A'
                      }
                      readOnly
                      style={{ backgroundColor: '#ffffff' }}
                    />
                  </Col>
                )}
                {submittedGlaucomaDetails.eyeProblemStatus && (
                  <Col md={3}>
                    <Form.Label style={{ fontWeight: 'bold' }}>Problem Status:</Form.Label>
                    <Form.Control
                      type="text"
                      value={submittedGlaucomaDetails.eyeProblemStatus.status}
                      readOnly
                      style={{ backgroundColor: '#ffffff' }}
                    />
                  </Col>
                )}
                {submittedGlaucomaDetails.glaucomaReferredHospital && (
                  <Col md={3}>
                    <Form.Label style={{ fontWeight: 'bold' }}>Referred Hospital:</Form.Label>
                    <Form.Control
                      type="text"
                      value={submittedGlaucomaDetails.glaucomaReferredHospital.hospitalName}
                      readOnly
                      style={{ backgroundColor: '#ffffff' }}
                    />
                  </Col>
                )}
              </Row>

              {/* Prescribed Medication Details */}
              {submittedGlaucomaDetails.treatmentType === 'prescribedMedication' && submittedGlaucomaDetails.prescribedMedication && (
                <Row className="mb-2">
                  <Col md={4}>
                    <Form.Label style={{ fontWeight: 'bold' }}>Prescribed Medication:</Form.Label>
                    <Form.Control
                      type="text"
                      value={submittedGlaucomaDetails.prescribedMedication}
                      readOnly
                      style={{ backgroundColor: '#ffffff' }}
                    />
                  </Col>
                </Row>
              )}

              {/* Treatment Completion Details - Show for all treatment types if present */}
              {(submittedGlaucomaDetails.treatmentCompletionDate || submittedGlaucomaDetails.treatmentCompletionRemarks) && (
                <Row className="mb-2">
                  {submittedGlaucomaDetails.treatmentCompletionDate && (
                    <Col md={4}>
                      <Form.Label style={{ fontWeight: 'bold' }}>Treatment Completion Date:</Form.Label>
                      <Form.Control
                        type="text"
                        value={submittedGlaucomaDetails.treatmentCompletionDate}
                        readOnly
                        style={{ backgroundColor: '#ffffff' }}
                      />
                    </Col>
                  )}
                  {submittedGlaucomaDetails.treatmentCompletionRemarks && (
                    <Col md={8}>
                      <Form.Label style={{ fontWeight: 'bold' }}>Treatment Completion Remarks:</Form.Label>
                      <Form.Control
                        type="text"
                        value={submittedGlaucomaDetails.treatmentCompletionRemarks}
                        readOnly
                        style={{ backgroundColor: '#ffffff' }}
                      />
                    </Col>
                  )}
                </Row>
              )}

              {/* Surgery/Laser Rx Details */}
              {(submittedGlaucomaDetails.treatmentType === 'surgery' || submittedGlaucomaDetails.treatmentType === 'laserRx') && (
                <>
                  <Row className="mb-2">
                    <Col md={3}>
                      <Form.Label style={{ fontWeight: 'bold' }}>Date of Admission:</Form.Label>
                      <Form.Control
                        type="text"
                        value={submittedGlaucomaDetails.dateOfAdmission ? formatDate(submittedGlaucomaDetails.dateOfAdmission) : 'N/A'}
                        readOnly
                        style={{ backgroundColor: '#ffffff' }}
                      />
                    </Col>
                    <Col md={3}>
                      <Form.Label style={{ fontWeight: 'bold' }}>Date of Surgery:</Form.Label>
                      <Form.Control
                        type="text"
                        value={submittedGlaucomaDetails.dateOfSurgery ? formatDate(submittedGlaucomaDetails.dateOfSurgery) : 'N/A'}
                        readOnly
                        style={{ backgroundColor: '#ffffff' }}
                      />
                    </Col>
                    <Col md={3}>
                      <Form.Label style={{ fontWeight: 'bold' }}>Discharge Date:</Form.Label>
                      <Form.Control
                        type="text"
                        value={submittedGlaucomaDetails.dischargeDate ? formatDate(submittedGlaucomaDetails.dischargeDate) : 'N/A'}
                        readOnly
                        style={{ backgroundColor: '#ffffff' }}
                      />
                    </Col>
                    <Col md={3}>
                      <Form.Label style={{ fontWeight: 'bold' }}>Procedure Done:</Form.Label>
                      <Form.Control
                        type="text"
                        value={submittedGlaucomaDetails.procedureDone || 'N/A'}
                        readOnly
                        style={{ backgroundColor: '#ffffff' }}
                      />
                    </Col>
                  </Row>
                  {submittedGlaucomaDetails.insurance && (
                    <Row className="mb-2">
                      <Col md={6}>
                        <Form.Label style={{ fontWeight: 'bold' }}>Insurance:</Form.Label>
                        <Form.Control
                          type="text"
                          value={submittedGlaucomaDetails.insurance || 'N/A'}
                          readOnly
                          style={{ backgroundColor: '#ffffff' }}
                        />
                      </Col>
                    </Row>
                  )}
                </>
              )}

              {/* Patient Refused Treatment */}
              {submittedGlaucomaDetails.treatmentType === 'patientRefusedTreatment' && submittedGlaucomaDetails.reasonForRefusal && (
                <Row className="mb-2">
                  <Col md={6}>
                    <Form.Label style={{ fontWeight: 'bold' }}>Reason for Refusal:</Form.Label>
                    <Form.Control
                      type="text"
                      value={submittedGlaucomaDetails.reasonForRefusal}
                      readOnly
                      style={{ backgroundColor: '#ffffff' }}
                    />
                  </Col>
                </Row>
              )}
            </div>
          )}
          
          {/* Display Follow-up History */}
          {glaucomaFollowUpDetails && glaucomaFollowUpDetails.length > 0 && (
            <div className="mb-4">
              {/* <h6>Follow-up History:</h6> */}
              {glaucomaFollowUpDetails.map((followUp, index) => (
                <div key={index}>
                  <Row className="mb-2">
                    <Col md={followUp.actualVisitDate ? 2 : 3}>
                      <Form.Label style={{ fontWeight: 'bold' }}>Follow-up Date {index + 1}:</Form.Label>
                      <Form.Control
                        type="text"
                        value={formatDate(followUp.followUpDate)}
                        readOnly
                        style={{ backgroundColor: '#f8f9fa' }}
                      />
                    </Col>
                    {followUp.actualVisitDate && (
                      <Col md={2}>
                        <Form.Label style={{ fontWeight: 'bold' }}>Actual Visit Date:</Form.Label>
                        <Form.Control
                          type="text"
                          value={formatDate(followUp.actualVisitDate)}
                          readOnly
                          style={{ backgroundColor: '#f8f9fa' }}
                        />
                      </Col>
                    )}
                    <Col md={2}>
                      <Form.Label style={{ fontWeight: 'bold' }}>Place of Follow-up:</Form.Label>
                      <Form.Control
                        type="text"
                        value={followUp.placeOfFollowUp?.name || 'N/A'}
                        readOnly
                        style={{ backgroundColor: '#f8f9fa' }}
                      />
                    </Col>
                    <Col md={followUp.actualVisitDate ? 6 : 7}>
                      <Form.Label style={{ fontWeight: 'bold' }}>Remark:</Form.Label>
                      <Form.Control
                        type="text"
                        value={followUp.remarks || 'N/A'}
                        readOnly
                        style={{ backgroundColor: '#f8f9fa' }}
                      />
                    </Col>
                  </Row>
                </div>
              ))}
            </div>
          )}

          {/* Form to Add Actual Visit Date and Next Steps */}
          <Row className="mb-3">
            <Col md={4}>
              <Form.Label>Actual Visit Date <span style={{ color: 'red' }}>*</span>:</Form.Label>
              <Form.Control
                type="date"
                value={glaucomaData.actualVisitDate}
                min={glaucomaFollowUpDetails.length > 0 ? glaucomaFollowUpDetails[glaucomaFollowUpDetails.length - 1].followUpDate : undefined}
                onChange={(e) => {
                  const selectedDate = e.target.value;
                  const lastFollowUpDate = glaucomaFollowUpDetails.length > 0 
                    ? glaucomaFollowUpDetails[glaucomaFollowUpDetails.length - 1].followUpDate 
                    : null;
                  
                  if (lastFollowUpDate && selectedDate < lastFollowUpDate) {
                    showAlert('Validation Error', 'Actual visit date must be on or after the follow-up date.', 'warning');
                    return;
                  }
                  handleGlaucomaDataChange('actualVisitDate', selectedDate);
                }}
                required
              />
            </Col>
          </Row>

          <Row className="mb-3">
            <Col md={12}>
              <Form.Label>Treatment Status <span style={{ color: 'red' }}>*</span>:</Form.Label>
              <div className="d-flex gap-3">
                <Form.Check
                  type="radio"
                  id="glaucoma-status-followup"
                  name="treatmentStatus"
                  label="Follow Up"
                  checked={glaucomaData.treatmentStatus === 'followUp'}
                  onChange={() => handleGlaucomaDataChange('treatmentStatus', 'followUp')}
                />
                {submittedGlaucomaDetails.diagnosed === 'no' && (
                  <Form.Check
                    type="radio"
                    id="glaucoma-status-diagnosed-yes"
                    name="treatmentStatus"
                    label="Glaucoma Diagnosed (Yes)"
                    checked={glaucomaData.treatmentStatus === 'diagnosedYes'}
                    onChange={() => handleGlaucomaDataChange('treatmentStatus', 'diagnosedYes')}
                  />
                )}
                <Form.Check
                  type="radio"
                  id="glaucoma-status-completed"
                  name="treatmentStatus"
                  label="Treatment Completed"
                  checked={glaucomaData.treatmentStatus === 'treatmentCompleted'}
                  onChange={() => handleGlaucomaDataChange('treatmentStatus', 'treatmentCompleted')}
                />
              </div>
            </Col>
          </Row>

          {/* Follow Up Option */}
          {glaucomaData.treatmentStatus === 'followUp' && (
            <>
              <Row className="mb-3">
                <Col md={4}>
                  <Form.Label><span style={{ color: 'red' }}>*</span> Place of Follow-up :</Form.Label>
                  <Form.Select
                    value={glaucomaData.placeOfFollowUp}
                    onChange={(e) => handleGlaucomaDataChange('placeOfFollowUp', e.target.value)}
                    required
                  >
                    <option value="">Select Place</option>
                    {followupPlaces.map(place => (
                      <option key={place.id} value={place.id}>{place.name}</option>
                    ))}
                  </Form.Select>
                </Col>
                <Col md={4}>
                  <Form.Label><span style={{ color: 'red' }}>*</span> Next Follow-up Date :</Form.Label>
                  <Form.Control
                    type="date"
                    value={glaucomaData.followUpDate}
                    min={glaucomaData.actualVisitDate || undefined}
                    onChange={(e) => {
                      const selectedDate = e.target.value;
                      if (glaucomaData.actualVisitDate && selectedDate < glaucomaData.actualVisitDate) {
                        showAlert('Validation Error', 'Follow-up date must be after the actual visit date.', 'warning');
                        return;
                      }
                      handleGlaucomaDataChange('followUpDate', selectedDate);
                    }}
                    required
                  />
                </Col>
                <Col md={4}>
                  <Form.Label><span style={{ color: 'red' }}>*</span> Remarks :</Form.Label>
                  <Form.Control
                    type="text"
                    value={glaucomaData.remarks}
                    onChange={(e) => handleGlaucomaDataChange('remarks', e.target.value)}
                    placeholder="Enter remarks"
                    required
                  />
                </Col>
              </Row>
            </>
          )}

          {/* Glaucoma Diagnosed Yes Option - Show treatment type selection */}
          {glaucomaData.treatmentStatus === 'diagnosedYes' && (
            <>
              <Row className="mb-3">
                <Col md={12}>
                  <Form.Label><span style={{ color: 'red' }}>*</span> Treatment Type :</Form.Label>
                  <div className="d-flex gap-3">
                    <Form.Check
                      type="radio"
                      id="glaucoma-diagnosed-medication"
                      name="diagnosedTreatmentType"
                      label="Prescribed Medication"
                      checked={glaucomaData.treatmentType === 'prescribedMedication'}
                      onChange={() => handleGlaucomaDataChange('treatmentType', 'prescribedMedication')}
                    />
                    <Form.Check
                      type="radio"
                      id="glaucoma-diagnosed-laser"
                      name="diagnosedTreatmentType"
                      label="Laser Rx"
                      checked={glaucomaData.treatmentType === 'laserRx'}
                      onChange={() => handleGlaucomaDataChange('treatmentType', 'laserRx')}
                    />
                    <Form.Check
                      type="radio"
                      id="glaucoma-diagnosed-surgery"
                      name="diagnosedTreatmentType"
                      label="Surgery"
                      checked={glaucomaData.treatmentType === 'surgery'}
                      onChange={() => handleGlaucomaDataChange('treatmentType', 'surgery')}
                    />
                    <Form.Check
                      type="radio"
                      id="glaucoma-diagnosed-refused"
                      name="diagnosedTreatmentType"
                      label="Patient Refused Treatment"
                      checked={glaucomaData.treatmentType === 'patientRefusedTreatment'}
                      onChange={() => handleGlaucomaDataChange('treatmentType', 'patientRefusedTreatment')}
                    />
                  </div>
                </Col>
              </Row>

              {/* Show appropriate fields based on treatment type */}
              {glaucomaData.treatmentType === 'prescribedMedication' && (
                <>
                  <Row className="mb-3">
                    <Col md={4}>
                      <Form.Label>Prescribed medication :</Form.Label>
                      <Form.Select
                        value={glaucomaData.prescribedMedication}
                        onChange={(e) => handleGlaucomaDataChange('prescribedMedication', e.target.value)}
                      >
                        <option value="">Select Medication</option>
                        <option value="Timolol">Timolol</option>
                        <option value="Latanoprost">Latanoprost</option>
                        <option value="Dorzolamide">Dorzolamide</option>
                        <option value="Others - To specify">Others - To specify</option>
                      </Form.Select>
                    </Col>
                    <Col md={4}>
                      <Form.Label><span style={{ color: 'red' }}>*</span> Place of Follow-up :</Form.Label>
                      <Form.Select
                        value={glaucomaData.placeOfFollowUp}
                        onChange={(e) => handleGlaucomaDataChange('placeOfFollowUp', e.target.value)}
                        required
                      >
                        <option value="">Select Place</option>
                        {followupPlaces.map(place => (
                          <option key={place.id} value={place.id}>{place.name}</option>
                        ))}
                      </Form.Select>
                    </Col>
                    <Col md={4}>
                      <Form.Label><span style={{ color: 'red' }}>*</span> Follow-up Date :</Form.Label>
                      <Form.Control
                        type="date"
                        value={glaucomaData.followUpDate}
                        min={glaucomaData.actualVisitDate || undefined}
                        onChange={(e) => {
                          const selectedDate = e.target.value;
                          if (glaucomaData.actualVisitDate && selectedDate < glaucomaData.actualVisitDate) {
                            showAlert('Validation Error', 'Follow-up date must be on or after the actual visit date.', 'warning');
                            return;
                          }
                          handleGlaucomaDataChange('followUpDate', selectedDate);
                        }}
                        required
                      />
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={12}>
                      <Form.Label><span style={{ color: 'red' }}>*</span> Remarks :</Form.Label>
                      <Form.Control
                        type="text"
                        value={glaucomaData.remarks}
                        onChange={(e) => handleGlaucomaDataChange('remarks', e.target.value)}
                        placeholder="Enter remarks"
                        required
                      />
                    </Col>
                  </Row>
                </>
              )}

              {glaucomaData.treatmentType === 'patientRefusedTreatment' && (
                <Row className="mb-3">
                  <Col md={12}>
                    <Form.Label>Reason for Refusal :</Form.Label>
                    <Form.Control
                      type="text"
                      value={glaucomaData.reasonForRefusal}
                      onChange={(e) => handleGlaucomaDataChange('reasonForRefusal', e.target.value)}
                      placeholder="Enter reason"
                    />
                  </Col>
                </Row>
              )}
            </>
          )}

          {/* Treatment Completed Option */}
          {glaucomaData.treatmentStatus === 'treatmentCompleted' && (
            <Row className="mb-3">
              <Col md={4}>
                <Form.Label><span style={{ color: 'red' }}>*</span> Treatment Completion Date :</Form.Label>
                <Form.Control
                  type="date"
                  value={glaucomaData.treatmentCompletionDate}
                  min={glaucomaData.actualVisitDate || undefined}
                  onChange={(e) => {
                    const selectedDate = e.target.value;
                    if (glaucomaData.actualVisitDate && selectedDate < glaucomaData.actualVisitDate) {
                      showAlert('Validation Error', 'Treatment completion date must be on or after the actual visit date.', 'warning');
                      return;
                    }
                    handleGlaucomaDataChange('treatmentCompletionDate', selectedDate);
                  }}
                  required
                />
              </Col>
              <Col md={8}>
                <Form.Label><span style={{ color: 'red' }}>*</span> Remarks :</Form.Label>
                <Form.Control
                  type="text"
                  value={glaucomaData.treatmentCompletionRemarks}
                  onChange={(e) => handleGlaucomaDataChange('treatmentCompletionRemarks', e.target.value)}
                  placeholder="Enter remarks"
                  required
                />
              </Col>
            </Row>
          )}

          {/* Submit Button for Follow-up */}
          <Row className="mt-4">
            <Col className="d-flex justify-content-end">
              <Button 
                variant="success" 
                onClick={handleGlaucomaSubmit}
                disabled={
                  submitLoading || 
                  !glaucomaData.actualVisitDate ||
                  !glaucomaData.treatmentStatus ||
                  (glaucomaData.treatmentStatus === 'followUp' && (!glaucomaData.placeOfFollowUp || !glaucomaData.followUpDate || !glaucomaData.remarks)) ||
                  (glaucomaData.treatmentStatus === 'diagnosedYes' && (
                    !glaucomaData.treatmentType ||
                    (glaucomaData.treatmentType === 'prescribedMedication' && (!glaucomaData.placeOfFollowUp || !glaucomaData.followUpDate || !glaucomaData.remarks)) ||
                    (glaucomaData.treatmentType === 'laserRx' && false) ||
                    (glaucomaData.treatmentType === 'surgery' && false) ||
                    (glaucomaData.treatmentType === 'patientRefusedTreatment' && false)
                  )) ||
                  (glaucomaData.treatmentStatus === 'treatmentCompleted' && (!glaucomaData.treatmentCompletionDate || !glaucomaData.treatmentCompletionRemarks))
                }
                style={{ 
                  padding: '0.5rem 2rem',
                  fontSize: '1rem'
                }}
              >
                {submitLoading ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                      className="me-2"
                    />
                    Submitting...
                  </>
                ) : (
                  'Submit Follow-up'
                )}
              </Button>
            </Col>
          </Row>
        </>
      )}

      {/* Read-only Display of Submitted Glaucoma Details - Only show if NOT in follow-up management mode */}
      {submittedGlaucomaDetails && 
        !(submittedGlaucomaDetails.eyeProblemStatus && submittedGlaucomaDetails.eyeProblemStatus.id === 1) &&
        !(submittedGlaucomaDetails.diagnosed === 'no' && glaucomaFollowUpDetails.length > 0) &&
        !(glaucomaFollowUpDetails.length > 0 && (!submittedGlaucomaDetails.eyeProblemStatus || submittedGlaucomaDetails.eyeProblemStatus.id !== 2)) && (
        <> 
          <hr className="my-4" />
          <h5 className="mb-3">Glaucoma Details</h5>
          
          {/* Display only non-null values */}
          <Row className="mb-3">
            {submittedGlaucomaDetails.testConducted && submittedGlaucomaDetails.testConducted.length > 0 && (
              <Col md={12}>
                <Form.Label style={{ fontWeight: 'bold' }}>Test Conducted:</Form.Label>
                <Form.Control
                  type="text"
                  value={Array.isArray(submittedGlaucomaDetails.testConducted) 
                    ? submittedGlaucomaDetails.testConducted.join(', ') 
                    : submittedGlaucomaDetails.testConducted}
                  readOnly
                  style={{ backgroundColor: '#f8f9fa' }}
                />
              </Col>
            )}
          </Row>
          
          <Row className="mb-3">
            <Col md={3}>
              <Form.Label style={{ fontWeight: 'bold' }}>Diagnosed:</Form.Label>
              <Form.Control
                type="text"
                value={submittedGlaucomaDetails.diagnosed === 'yes' ? 'Yes' : 'No'}
                readOnly
                style={{ backgroundColor: '#f8f9fa' }}
              />
            </Col>
            {submittedGlaucomaDetails.treatmentType && (
              <Col md={3}>
                <Form.Label style={{ fontWeight: 'bold' }}>Treatment Type:</Form.Label>
                <Form.Control
                  type="text"
                  value={
                    submittedGlaucomaDetails.treatmentType === 'prescribedMedication' ? 'Prescribed Medication' :
                    submittedGlaucomaDetails.treatmentType === 'laserRx' ? 'Laser Rx' :
                    submittedGlaucomaDetails.treatmentType === 'surgery' ? 'Surgery' :
                    submittedGlaucomaDetails.treatmentType === 'patientRefusedTreatment' ? 'Patient Refused Treatment' :
                    submittedGlaucomaDetails.treatmentType
                  }
                  readOnly
                  style={{ backgroundColor: '#f8f9fa' }}
                />
              </Col>
            )}
            {submittedGlaucomaDetails.eyeProblemStatus && (
              <Col md={3}>
                <Form.Label style={{ fontWeight: 'bold' }}>Status:</Form.Label>
                <Form.Control
                  type="text"
                  value={submittedGlaucomaDetails.eyeProblemStatus.status}
                  readOnly
                  style={{ backgroundColor: '#f8f9fa' }}
                />
              </Col>
            )}
            {submittedGlaucomaDetails.glaucomaReferredHospital && (
              <Col md={3}>
                <Form.Label style={{ fontWeight: 'bold' }}>Referred Hospital:</Form.Label>
                <Form.Control
                  type="text"
                  value={submittedGlaucomaDetails.glaucomaReferredHospital.hospitalName}
                  readOnly
                  style={{ backgroundColor: '#f8f9fa' }}
                />
              </Col>
            )}
          </Row>
          
          {/* Prescribed Medication Fields */}
          {submittedGlaucomaDetails.treatmentType === 'prescribedMedication' && submittedGlaucomaDetails.prescribedMedication && (
            <Row className="mb-3">
              <Col md={4}>
                <Form.Label style={{ fontWeight: 'bold' }}>Prescribed Medication:</Form.Label>
                <Form.Control
                  type="text"
                  value={submittedGlaucomaDetails.prescribedMedication}
                  readOnly
                  style={{ backgroundColor: '#f8f9fa' }}
                />
              </Col>
            </Row>
          )}

          {/* Treatment Completion Details - Show for all treatment types if present */}
          {(submittedGlaucomaDetails.treatmentCompletionDate || submittedGlaucomaDetails.treatmentCompletionRemarks) && (
            <Row className="mb-3">
              {submittedGlaucomaDetails.treatmentCompletionDate && (
                <Col md={4}>
                  <Form.Label style={{ fontWeight: 'bold' }}>Treatment Completion Date:</Form.Label>
                  <Form.Control
                    type="text"
                    value={formatDDMMYYYY(submittedGlaucomaDetails.treatmentCompletionDate)}
                    readOnly
                    style={{ backgroundColor: '#f8f9fa' }}
                  />
                </Col>
              )}
              {submittedGlaucomaDetails.treatmentCompletionRemarks && (
                <Col md={8}>
                  <Form.Label style={{ fontWeight: 'bold' }}>Treatment Completion Remarks:</Form.Label>
                  <Form.Control
                    type="text"
                    value={submittedGlaucomaDetails.treatmentCompletionRemarks}
                    readOnly
                    style={{ backgroundColor: '#f8f9fa' }}
                  />
                </Col>
              )}
            </Row>
          )}

          {/* Laser Rx and Surgery Fields - Only show non-null values */}
          {(submittedGlaucomaDetails.treatmentType === 'laserRx' || submittedGlaucomaDetails.treatmentType === 'surgery') && (
            <>
              <Row className="mb-3">
                {submittedGlaucomaDetails.dateOfAdmission && (
                  <Col md={3}>
                    <Form.Label style={{ fontWeight: 'bold' }}>Date of Admission:</Form.Label>
                    <Form.Control
                      type="text"
                      value={formatDate(submittedGlaucomaDetails.dateOfAdmission)}
                      readOnly
                      style={{ backgroundColor: '#f8f9fa' }}
                    />
                  </Col>
                )}
                {submittedGlaucomaDetails.dateOfSurgery && (
                  <Col md={3}>
                    <Form.Label style={{ fontWeight: 'bold' }}>Date of Surgery:</Form.Label>
                    <Form.Control
                      type="text"
                      value={formatDate(submittedGlaucomaDetails.dateOfSurgery)}
                      readOnly
                      style={{ backgroundColor: '#f8f9fa' }}
                    />
                  </Col>
                )}
                {submittedGlaucomaDetails.dischargeDate && (
                  <Col md={3}>
                    <Form.Label style={{ fontWeight: 'bold' }}>Discharge Date:</Form.Label>
                    <Form.Control
                      type="text"
                      value={formatDate(submittedGlaucomaDetails.dischargeDate)}
                      readOnly
                      style={{ backgroundColor: '#f8f9fa' }}
                    />
                  </Col>
                )}
                {submittedGlaucomaDetails.procedureDone && (
                  <Col md={3}>
                    <Form.Label style={{ fontWeight: 'bold' }}>Procedure Done:</Form.Label>
                    <Form.Control
                      type="text"
                      value={submittedGlaucomaDetails.procedureDone}
                      readOnly
                      style={{ backgroundColor: '#f8f9fa' }}
                    />
                  </Col>
                )}
              </Row>
              {(submittedGlaucomaDetails.dateOfFollowUpAfter || submittedGlaucomaDetails.referredHospital || submittedGlaucomaDetails.insurance) && (
                <Row className="mb-3">
                  {submittedGlaucomaDetails.dateOfFollowUpAfter && (
                    <Col md={4}>
                      <Form.Label style={{ fontWeight: 'bold' }}>Date of Follow Up After:</Form.Label>
                      <Form.Control
                        type="text"
                        value={submittedGlaucomaDetails.dateOfFollowUpAfter}
                        readOnly
                        style={{ backgroundColor: '#f8f9fa' }}
                      />
                    </Col>
                  )}
                  {submittedGlaucomaDetails.referredHospital && (
                    <Col md={4}>
                      <Form.Label style={{ fontWeight: 'bold' }}>Referred Hospital:</Form.Label>
                      <Form.Control
                        type="text"
                        value={submittedGlaucomaDetails.referredHospital}
                        readOnly
                        style={{ backgroundColor: '#f8f9fa' }}
                      />
                    </Col>
                  )}
                  {submittedGlaucomaDetails.insurance && (
                    <Col md={4}>
                      <Form.Label style={{ fontWeight: 'bold' }}>Insurance:</Form.Label>
                      <Form.Control
                        type="text"
                        value={submittedGlaucomaDetails.insurance}
                        readOnly
                        style={{ backgroundColor: '#f8f9fa' }}
                      />
                    </Col>
                  )}
                </Row>
              )}
            </>
          )}

          {/* Patient Refused Treatment Fields - Only show if not null */}
          {submittedGlaucomaDetails.treatmentType === 'patientRefusedTreatment' && submittedGlaucomaDetails.reasonForRefusal && (
            <Row className="mb-3">
              <Col md={6}>
                <Form.Label style={{ fontWeight: 'bold' }}>Reason for Refusal:</Form.Label>
                <Form.Control
                  type="text"
                  value={submittedGlaucomaDetails.reasonForRefusal}
                  readOnly
                  style={{ backgroundColor: '#f8f9fa' }}
                />
              </Col>
            </Row>
          )}

          {/* No Diagnosis - Follow Up Period - Only show if not null */}
          {submittedGlaucomaDetails.diagnosed === 'no' && submittedGlaucomaDetails.followUpPeriod && (
            <Row className="mb-3">
              <Col md={4}>
                <Form.Label style={{ fontWeight: 'bold' }}>Follow Up Period:</Form.Label>
                <Form.Control
                  type="text"
                  value={submittedGlaucomaDetails.followUpPeriod}
                  readOnly
                  style={{ backgroundColor: '#f8f9fa' }}
                />
              </Col>
            </Row>
          )}

          {/* Display Follow-up History for Completed Treatments */}
          {glaucomaFollowUpDetails && glaucomaFollowUpDetails.length > 0 && (
            <>
              <hr className="my-3" />
              <h6 className="mb-3">Follow-up History</h6>
              {glaucomaFollowUpDetails.map((followUp, index) => (
                <div key={index}>
                  <Row className="mb-2">
                    <Col md={followUp.actualVisitDate ? 2 : 3}>
                      <Form.Label style={{ fontWeight: 'bold' }}>Follow-up Date {index + 1}:</Form.Label>
                      <Form.Control
                        type="text"
                        value={formatDate(followUp.followUpDate)}
                        readOnly
                        style={{ backgroundColor: '#f8f9fa' }}
                      />
                    </Col>
                    {followUp.actualVisitDate && (
                      <Col md={2}>
                        <Form.Label style={{ fontWeight: 'bold' }}>Actual Visit Date:</Form.Label>
                        <Form.Control
                          type="text"
                          value={formatDate(followUp.actualVisitDate)}
                          readOnly
                          style={{ backgroundColor: '#f8f9fa' }}
                        />
                      </Col>
                    )}
                    <Col md={2}>
                      <Form.Label style={{ fontWeight: 'bold' }}>Place of Follow-up:</Form.Label>
                      <Form.Control
                        type="text"
                        value={followUp.placeOfFollowUp?.name || 'N/A'}
                        readOnly
                        style={{ backgroundColor: '#f8f9fa' }}
                      />
                    </Col>
                    <Col md={followUp.actualVisitDate ? 6 : 7}>
                      <Form.Label style={{ fontWeight: 'bold' }}>Remark:</Form.Label>
                      <Form.Control
                        type="text"
                        value={followUp.remarks || 'N/A'}
                        readOnly
                        style={{ backgroundColor: '#f8f9fa' }}
                      />
                    </Col>
                  </Row>
                </div>
              ))}
            </>
          )}
        </>
      )}
    </>
  );
};

export default GlaucomaDetailsCard;
